import { ApiError } from "../../../api/api-client";
import { readMintHistory, type MintHistoryContext, type MintHistoryPage } from "../api/mint-history";

export interface MintHistoryState {
  page: MintHistoryPage | null;
  pending: "initial" | "refresh" | "more" | null;
  error: { operation: "initial" | "refresh" | "more"; message: string } | null;
  notice: string | null;
}

/** A cursor chain belongs only to one API/deployment context, never a wallet. */
export class MintHistoryController {
  private state: MintHistoryState = { page: null, pending: null, error: null, notice: null };
  private listeners = new Set<() => void>();
  private request: AbortController | null = null;
  private generation = 0;
  constructor(private readonly context: MintHistoryContext) {}
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private publish(patch: Partial<MintHistoryState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach(listener => listener());
  }
  private unexpected = () => { this.publish({ pending: null, error: { operation: "initial", message: "Mint history could not be updated. Try refreshing." } }); };
  start = () => { this.refresh().catch(this.unexpected); };
  stop = () => { this.generation += 1; this.request?.abort(); this.request = null; };
  refresh = () => this.fetchPage(false);
  loadMore = () => this.fetchPage(true);

  private async fetchPage(more: boolean, recovering = false): Promise<void> {
    if (this.request || more && !this.state.page?.nextCursor) return;
    const cursor = more ? this.state.page!.nextCursor : null;
    const operation = more ? "more" : this.state.page ? "refresh" : "initial";
    const request = new AbortController();
    const generation = ++this.generation;
    this.request = request;
    this.publish({ pending: operation, error: null, ...(recovering ? {} : { notice: null }) });
    try {
      const page = await readMintHistory(this.context, cursor, request.signal);
      if (generation !== this.generation) return;
      const seen = new Set<string>();
      const items = page.items === null ? null : [...(more ? this.state.page?.items ?? [] : []), ...page.items]
        .filter(item => { if (seen.has(item.id)) return false; seen.add(item.id); return true; });
      this.publish({ page: { ...page, items }, pending: null });
    } catch (error) {
      if (generation !== this.generation || request.signal.aborted) return;
      if (error instanceof ApiError && error.status === 409 && error.code === "NFT_HISTORY_CURSOR_REORGED" && !recovering) {
        // Drop the invalid chain before recovery, including its cursor. Never append across reorgs.
        this.request = null;
        this.publish({ page: null, pending: null, notice: "Chain reorganization detected. Reloading confirmed history from the first page." });
        await this.fetchPage(false, true);
        return;
      }
      const message = error instanceof ApiError && error.status === 429 ? "Too many requests. Wait before trying again." :
        error instanceof ApiError && error.status === 400 ? "History query or cursor was rejected. Refresh to start again." :
        error instanceof ApiError && error.status >= 500 && error.code !== "NFT_HISTORY_CONTEXT_MISMATCH" && error.code !== "INVALID_SERVER_RESPONSE" ?
          `Mint history is temporarily unavailable from the server (HTTP ${error.status}). Try refreshing later.` :
        error instanceof ApiError ? `History request failed (${error.status}): ${error.message}` : "Could not reach mint history. Check your connection and try again.";
      this.publish({ pending: null, error: { operation, message } });
    } finally {
      if (this.request === request) this.request = null;
    }
  }
}
